import { useMemo, useState } from "react"
import { X, Mail } from "lucide-react"

import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"
import Checkbox from "../../../../components/ui/checkbox/checkbox"
import { discountChosenLabel } from "../new-quote/itinerary-timeline"

import "./send-channel-modal.scss"

/**
 * Intermediate "how would you like to send this quote?" picker, shown when the
 * Send action on a draft card is clicked. Email opens the quote-email modal;
 * WhatsApp is a disabled "coming soon" placeholder for now.
 *
 * Deliberately light - two icon-topped cards side by side with a little motion,
 * rather than a plain list.
 *
 * Before a channel can be chosen the agent sets how long the quote stays valid
 * (a number + hours/days/weeks, defaulting to 24 hours). That validity is handed
 * to onEmail so the quote-email modal can stamp the "valid until" date.
 */

// Validity units for the Select (keys double as moment .add() units).
const UNIT_OPTIONS = { hours: "Hours", days: "Days", weeks: "Weeks" }

// Full-balance threshold (weeks before travel). Within this window of travel the
// client pays the full balance now rather than a deposit, and it's also when the
// remaining balance falls due. Default 12; keys are weeks.
const FULL_BALANCE_OPTIONS = { 8: "8 weeks", 12: "12 weeks", 14: "14 weeks" }
const DEFAULT_FULL_BALANCE_WEEKS = 12

// WhatsApp glyph (lucide dropped brand icons), inline so it tints via currentColor.
const WhatsAppGlyph = ({ size = 26 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
	</svg>
)

export default function SendChannelModal({ draft, onClose, onEmail }) {
	// How long the quote stays valid. Default 24 hours; the agent must leave a
	// valid (>= 1) amount for either channel to become clickable.
	const [amount, setAmount] = useState("24")
	const [unit, setUnit] = useState("hours")

	// "Quote only": hide the book-now buttons on the public page so the client
	// can't self-serve a booking - they're pointed at the sales agent instead.
	const [bookDisabled, setBookDisabled] = useState(false)

	// Full-balance threshold (weeks) for this quote; drives deposit-vs-full-balance
	// on the public page and the authoritative deposit charge.
	const [fullBalanceWeeks, setFullBalanceWeeks] = useState(DEFAULT_FULL_BALANCE_WEEKS)

	// Applied discounts (one row per item + discount the agent ticked in the
	// builder). These are already baked into the price; here the agent picks which
	// to ADVERTISE on the public quote. A row keyed "{itemUid}:{discountId}".
	const discountRows = useMemo(() => {
		const basket = Array.isArray(draft?.basket) ? draft.basket : []
		const rows = []
		basket.forEach((item) => {
			if (item.type !== "hotel" && item.type !== "round") return
			;(Array.isArray(item.discountsChosen) ? item.discountsChosen : []).forEach((discount) => {
				rows.push({
					key: `${item.uid}:${discount.id}`,
					itemTitle: item.title || (item.type === "hotel" ? "Hotel" : "Golf round"),
					label: discountChosenLabel(discount),
				})
			})
		})
		return rows
	}, [draft])

	// Keys the agent has unticked = NOT shown on the public quote. Default empty
	// (every applied discount is advertised). Display-only; never affects price.
	const [hiddenDiscounts, setHiddenDiscounts] = useState(() => new Set())
	const toggleDiscount = (key) =>
		setHiddenDiscounts((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})

	const amountNum = parseInt(amount, 10)
	const validityChosen = Number.isInteger(amountNum) && amountNum >= 1

	// Deliberately NO Escape / outside-click close: the quote modals only close
	// via the X or their action buttons, so in-progress settings can't be lost.

	const handleEmail = () => {
		if (!validityChosen) return
		onEmail({ amount: amountNum, unit, bookDisabled, fullBalanceWeeks, hiddenDiscounts: Array.from(hiddenDiscounts) })
	}

	return (
		<div className="send-channel-overlay">
			<div className="send-channel-modal" role="dialog" aria-modal="true" aria-label="Choose how to send this quote">
				<div className="send-channel-header">
					<h3 className="send-channel-title">Send method &amp; settings</h3>
					<button type="button" className="send-channel-close" onClick={onClose} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="send-channel-quoteonly">
					<Checkbox label="Quote only - hide the book-now buttons" checked={bookDisabled} onClick={() => setBookDisabled((value) => !value)} />
				</div>

				<div className="send-channel-validity">
					<span className="send-channel-validity-label">Quote valid for</span>
					<div className="send-channel-validity-fields">
						<Input
							type="number"
							wrapperClass="send-channel-validity-amount"
							value={amount}
							onChange={(value) => setAmount(value)}
							disabled={bookDisabled}
						/>
						<Select
							className="send-channel-validity-unit"
							options={UNIT_OPTIONS}
							selected={unit}
							placeholder="Unit"
							onSelect={({ option }) => setUnit(option)}
							disabled={bookDisabled}
						/>
					</div>
				</div>

				<div className={["send-channel-balance", bookDisabled ? "is-disabled" : ""].join(" ").trim()}>
					<span className="send-channel-validity-label">Full balance due</span>
					<Select
						className="send-channel-balance-weeks"
						options={FULL_BALANCE_OPTIONS}
						selected={String(fullBalanceWeeks)}
						placeholder="Full balance"
						onSelect={({ option }) => setFullBalanceWeeks(Number(option))}
						disabled={bookDisabled}
					/>
				</div>

				{discountRows.length > 0 && (
					<div className="send-channel-discounts">
						<span className="send-channel-validity-label">Offers to show on the quote</span>
						<p className="send-channel-discounts-hint">Ticked offers appear in the summary and on the item they apply to. Unticking only hides an offer - it never changes the price.</p>
						<div className="send-channel-discounts-list">
							{discountRows.map((row) => (
								<div className="send-channel-discount" key={row.key}>
									<Checkbox label={row.label} checked={!hiddenDiscounts.has(row.key)} onClick={() => toggleDiscount(row.key)} />
									<span className="send-channel-discount-item">Applied to {row.itemTitle}</span>
								</div>
							))}
						</div>
					</div>
				)}

				<div className="send-channel-options">
					<button
						type="button"
						className={["send-channel-option", validityChosen ? "" : "is-disabled"].join(" ").trim()}
						onClick={handleEmail}
						disabled={!validityChosen}>
						<span className="send-channel-icon is-email">
							<Mail size={26} />
						</span>
						<span className="send-channel-label">Email</span>
						<span className="send-channel-desc">Send a branded quote email</span>
					</button>

					<button type="button" className="send-channel-option is-disabled" disabled aria-label="WhatsApp (coming soon)">
						<span className="send-channel-badge">Coming soon</span>
						<span className="send-channel-icon is-whatsapp">
							<WhatsAppGlyph size={26} />
						</span>
						<span className="send-channel-label">WhatsApp</span>
						<span className="send-channel-desc">Send via WhatsApp message</span>
					</button>
				</div>
			</div>
		</div>
	)
}
